import { Chip, ChipPicker, ListItem } from '@tableau/widgets';
import * as React from 'react';

import { Messages } from '../../gen-src/Localize';

export const maxTagLength = 255;

export const TAG_COUNT_DELIMITER = ',';
export const TAG_REPLACE_REGEX = /[",]/g;

/** Props for the Tags Editor. */
export type TagsEditorProps = React.HTMLAttributes<HTMLDivElement> & {
  /** The current state of the Tags Editor. */
  readonly editorState: TagsEditorState;

  /** Callback to update the editor state in the parent. This will be called by TagsEditor when the state needs to change.  */
  readonly setEditorState: React.Dispatch<React.SetStateAction<TagsEditorState>>;

  /** Async callback to search for matching tags to populate the editor's dropdown. */
  readonly searchMatchingTags: (partialText: string) => Promise<ReadonlyArray<string>>;

  /**
   * When this boolean is true, the chip picker will become disabled.
   * Its intended usage is to disable the editor while the tags are being saved.
   */
  readonly disableEditor: boolean;

  /** Optional prop to show a custom label on the ChipPicker. */
  readonly label?: string;

  /** number of selected items to apply the tag */
  readonly itemCount: number;
};

/** The state of the Tags Editor. This is intended to be stored by a parent component. */
export type TagsEditorState = {
  /** Text entered in the editor that isn't part of an existing tag. */
  readonly partialText: string | null;

  /** Matching tags to currently populate in the "suggestions" dropdown. */
  readonly suggestions: ListItem[];

  /** Tags along with count */
  readonly tags: Map<string, number>;
};

const TagsEditorTestId = 'tags-editor';

/**
 * This component wraps the ChipPicker widget with functionality specific to editing tags on
 * Tableau Server content items. It does not include save/cancel logic (which can be provided,
 * depending on the UX situation, with a helper such as `ReactConfirmActionDialog` or `useInlineEditor`),
 * nor does it provide API connectivity to get/save the tags (this is also instantiation-specific).
 *
 * This is a controlled component, hence state must be managed by the parent. This component was
 * designed to work with the state management logic of either `ReactConfirmActionDialog` or `useInlineEditor`.
 * In common usage, the parent can simply store the state updates provided by `setEditorState` with
 * no additional processing (i.e. pass the updates directly to the dialog's state handler).
 *
 * @param props See documentation for TagsEditorProps
 */
export function TagsEditor(propsDestructureBeforeUsing: TagsEditorProps): JSX.Element {
  const { setEditorState, editorState, label, disableEditor, searchMatchingTags, itemCount, ...divProps } = propsDestructureBeforeUsing;

  const onFilterItems = React.useCallback(
    (value: string) => setEditorState(
      prevState => ({ ...prevState, partialText: value })
    ),
    [ setEditorState ]
  );

  const onValueAdd = React.useCallback(
    (value: string) => {
      setEditorState(prevState => {
        const updated = new Map(prevState.tags);

        // number of selected items should always be greater than 0.
        // do not modify the tag state if selected items < 0
        if (itemCount > 0) {
          updated.set(value, itemCount);
        }
        return { ...prevState, tags: updated };
      });
    },
    [setEditorState, itemCount]
  );

  const onValuesChange = React.useCallback(
    (selectedValues: ReadonlyArray<string>) => {
      const tagCountMap: Map<string, number> = new Map<string, number>();

      // set the editor state based on tag format.
      selectedValues.forEach(val => {
        const tagName = val.split(TAG_COUNT_DELIMITER)[0];
        const tagCount = parseInt(val.split(TAG_COUNT_DELIMITER)[1], 10);
        // add items to the tagMap.
        tagCountMap.set(tagName, tagCount);
      });
      setEditorState(prevState => ({ ...prevState, tags: tagCountMap }));
    },
    [setEditorState]
  );

  const onChangeCapture = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const clean = value.replace(TAG_REPLACE_REGEX, '');
    event.target.value = clean.length > maxTagLength ? clean.slice(0, maxTagLength) : clean;
  }, []);

  // Change handler for partialText. Calls the API to get the tag suggestions.
  React.useEffect(
    () => {
      const value = editorState.partialText?.trim();

      if (!value) {
        setEditorState(prevState => ({ ...prevState, suggestions: [] }));
        return;
      } else {
        // this allows the input value to show up in the dropdown immediately,
        // but along with previously fetched suggestions to reduce flickering
        setEditorState(prevState => ({
          ...prevState,
          suggestions: [{ value, disabled: false }, ...prevState.suggestions.slice(1)],
        }));
      }

      let latestQuery = true;
      searchMatchingTags(value)
        .then(matchingTags => {
          if (latestQuery) {
            const suggestions = suggestionsToListItems(value, matchingTags);
            setEditorState(prevState => ({ ...prevState, suggestions }));
          }
        })
        .catch(_ => {
          return;
        });

      return () => {
        latestQuery = false;
      };
    },

    // This deps array was noncompliant before we enabled the lint rule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editorState.partialText]
  );

  const mapTagsToChips = (editorState: TagsEditorState): JSX.Element[] => {
    const tagWithCountChip: JSX.Element[] = [];
    const showCount = itemCount && itemCount > 1;
    editorState.tags?.forEach((tagCount, tagName) => tagWithCountChip.push(
      <Chip
        key={tagName}
        tooltip={tagName}
        value={`${tagName}${TAG_COUNT_DELIMITER}${tagCount}`}>
        {showCount ? `${tagName} (${tagCount})` : tagName}
      </Chip>
    ));
    return tagWithCountChip;
  };

  return (
    <ChipPicker
      {...divProps}
      label={label ?? ''}
      disabled={disableEditor}
      testId={TagsEditorTestId}
      listItems={editorState.suggestions}
      placeholder={Messages.AddATag()}
      onValueAdd={onValueAdd}
      onValuesChange={onValuesChange}
      onFilterItems={onFilterItems}
      onChangeCapture={onChangeCapture}
    >
      {mapTagsToChips(editorState)}
    </ChipPicker>
  );
}

function suggestionsToListItems(search: string, suggestions: ReadonlyArray<string>): ListItem[] {
  const mapped = suggestions.map(t => ({ value: t, disabled: false }));
  const match = mapped.findIndex(t => t.value === search);

  if (match > -1) {
    mapped.splice(match, 1);
  }

  mapped.unshift({ value: search, disabled: false });

  return mapped;
}

/* helper function that encloses each value with an extra set of quotation marks. */
const quote = (arr: string[]) : string[] => (arr.map(v => `"${v}"`));

/**
 * For most content items, we have separate APIs for adding and removing tags. This helper function
 * takes the diff between the old and the new tags lists to figure out which ones need to be added or removed.
 *
 * It also encloses each tag in an extra set of quotation marks, which is required for the API to properly
 * handle tags containing spaces.
 */
 export function getTagListsForVizportalApi(params: {
  oldTags: Map<string, number>;
  newTags: Map<string, number>;
}) : { tagsToAdd : string[], tagsToRemove: string[] } {
  const { oldTags, newTags } = params;

  const tagsToAdd : string[] = [];

  // construct tags to add
  // logic: add the tag to `tagstoAdd` array if the newTags array has an additional tag
  // or if the count of the tag is not the same.
  newTags.forEach((val,key) => {
    if(oldTags.has(key)){
      if(oldTags.get(key) !== val){
        tagsToAdd.push(key);
      }
    } else {
      tagsToAdd.push(key);
    }
  });

  // construct tags to remove
  // add the tag to `tagsToRemove` array if there are additional keys to be removed in the old tags.
  const tagsToRemove = Array.from(oldTags.keys()).filter(k => !newTags.has(k));

  return { tagsToAdd: quote(tagsToAdd), tagsToRemove: quote(tagsToRemove) };
}
